// Admin "Policies" authoring API client (Phase 5A).
//
// USE_REAL_API-gated like the other api/<x>.ts modules (apiUrl): real engine
// hits /api/v1/admin/agreements + /policies/meta; mock mode is intercepted by
// MSW at the same relative paths. Mirrors backend app/api/v1/agreements_admin.py.
//
// Error contract: FastAPI HTTPException dicts `{ detail: { code, message } }`.
// Reuses AgreementsError so callers can branch on the typed `code`
// (e.g. incomplete_localizations, version_immutable).

import { apiUrl } from '../lib/api'
import { AgreementsError } from './agreements'

export type PolicyLocalization = {
  locale: string
  content: string
  content_sha256: string
  updated_at: string
}

export type PolicyVersion = {
  id: string
  agreement_id: string
  version: number
  status: 'draft' | 'published' | 'retired'
  effective_at?: string | null
  reaccept_required: boolean
  published_at?: string | null
  present_locales: string[]
  missing_locales: string[]
  localizations: PolicyLocalization[]
}

export type ApplicabilityRule = {
  id?: string
  effect: 'require' | 'exempt'
  scope?: string | null
  org_type?: string | null
  program_attribute_type?: string | null
  program_attribute_match?: Record<string, unknown> | null
  org_id?: string | null
}

export type PolicyAgreement = {
  id: string
  key: string
  name: string
  per_user_ack?: boolean
  is_marketing_consent?: boolean
  created_at: string
  versions: PolicyVersion[]
  applicability: ApplicabilityRule[]
}

export type PoliciesMeta = {
  supported_locales: string[]
  controlling_locale: string
}

async function toError(res: Response): Promise<AgreementsError> {
  const body = (await res.json().catch(() => null)) as {
    detail?: { code?: string; message?: string } | string
  } | null
  const env = body && typeof body.detail === 'object' ? body.detail : null
  const code = env?.code ?? 'error'
  const message =
    env?.message ?? (typeof body?.detail === 'string' ? body.detail : `HTTP ${res.status}`)
  return new AgreementsError(code, message, res.status)
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) throw await toError(res)
  return (await res.json()) as T
}

export async function listPolicies(): Promise<PolicyAgreement[]> {
  return json(await fetch(apiUrl('/admin/agreements')))
}

export async function createPolicy(input: {
  key: string
  name: string
  per_user_ack?: boolean
  is_marketing_consent?: boolean
}): Promise<PolicyAgreement> {
  return json(
    await fetch(apiUrl('/admin/agreements'), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
    }),
  )
}

export async function createVersion(
  agreementId: string,
  input: { reaccept_required: boolean },
): Promise<PolicyVersion> {
  return json(
    await fetch(apiUrl(`/admin/agreements/${agreementId}/versions`), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
    }),
  )
}

export async function upsertLocalization(
  agreementId: string,
  versionId: string,
  locale: string,
  content: string,
): Promise<PolicyVersion> {
  return json(
    await fetch(
      apiUrl(`/admin/agreements/${agreementId}/versions/${versionId}/localizations/${locale}`),
      {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ content }),
      },
    ),
  )
}

export async function publishVersion(
  agreementId: string,
  versionId: string,
  effectiveAt?: string | null,
): Promise<PolicyVersion> {
  return json(
    await fetch(apiUrl(`/admin/agreements/${agreementId}/versions/${versionId}/publish`), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ effective_at: effectiveAt ?? null }),
    }),
  )
}

export async function retireVersion(
  agreementId: string,
  versionId: string,
): Promise<PolicyVersion> {
  return json(
    await fetch(apiUrl(`/admin/agreements/${agreementId}/versions/${versionId}/retire`), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: '{}',
    }),
  )
}

export async function setApplicability(
  agreementId: string,
  rules: ApplicabilityRule[],
): Promise<ApplicabilityRule[]> {
  return json(
    await fetch(apiUrl(`/admin/agreements/${agreementId}/applicability`), {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ rules }),
    }),
  )
}

export async function fetchAudienceCount(agreementId: string): Promise<{ count: number }> {
  return json(await fetch(apiUrl(`/admin/agreements/${agreementId}/audience-count`)))
}

export async function deleteDraftPolicy(agreementId: string): Promise<void> {
  const res = await fetch(apiUrl(`/admin/agreements/${agreementId}`), { method: 'DELETE' })
  if (!res.ok) throw await toError(res)
}
